import { useEffect } from "react";
import { SITE } from "../../data/site";
import { useI18n } from "../../i18n";

// Lightweight document-head manager (CRA, no SSR): sets title, meta,
// canonical, hreflang alternates, Open Graph, and injects JSON-LD schema.
function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href, hreflang) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    el.setAttribute("data-psv-seo", "true");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

// Only the production domain is indexable. Preview / emergentagent hosts are
// always noindex so pre-launch environments never enter the search index.
const PRODUCTION_HOST = "plastic-surgery-vietnam.com";
function isProductionHost() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === PRODUCTION_HOST || h === `www.${PRODUCTION_HOST}`;
}

export default function SEO({
  title,
  description,
  path = "/",
  noindex = false,
  type = "website",
  schema = [],
  ogImage = "/og-image.jpg",
}) {
  const { lang } = useI18n();

  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      setMeta("name", "description", description);
      setMeta("property", "og:description", description);
      setMeta("name", "twitter:description", description);
    }
    setMeta("property", "og:title", title || document.title);
    setMeta("property", "og:type", type);
    setMeta("property", "og:site_name", SITE.name);
    setMeta("property", "og:image", `${SITE.url}${ogImage}`);
    setMeta("property", "og:image:width", "1200");
    setMeta("property", "og:image:height", "630");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title || document.title);
    setMeta("name", "twitter:image", `${SITE.url}${ogImage}`);
    // Indexable only on the production host and only when not explicitly noindex.
    const robots = noindex || !isProductionHost() ? "noindex,follow" : "index,follow";
    setMeta("name", "robots", robots);

    document.documentElement.setAttribute("lang", lang);

    const cleanPath = path === "/" ? "" : path;
    // Canonical for current language.
    const prefix = lang === "en" ? "" : `/${lang}`;
    const canonical = `${SITE.url}${prefix}${cleanPath}/`.replace(/\/+$/, "/");
    setLink("canonical", canonical);
    setMeta("property", "og:url", canonical);

    // hreflang alternates for all languages + x-default.
    SITE.languages.forEach((l) => {
      const href = `${SITE.url}${l.prefix}${cleanPath}/`.replace(/\/+$/, "/");
      setLink("alternate", href, l.code);
    });
    setLink("alternate", `${SITE.url}${cleanPath}/`.replace(/\/+$/, "/"), "x-default");

    // JSON-LD schema.
    const existing = document.head.querySelectorAll('script[data-psv-schema="true"]');
    existing.forEach((n) => n.remove());
    (Array.isArray(schema) ? schema : [schema]).filter(Boolean).forEach((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-psv-schema", "true");
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
    });

    return () => {
      const scripts = document.head.querySelectorAll('script[data-psv-schema="true"]');
      scripts.forEach((n) => n.remove());
    };
  }, [title, description, path, noindex, type, lang, ogImage, schema]);

  return null;
}

// Reusable schema builders.
export const orgSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE.name,
  alternateName: "PSV",
  url: SITE.url,
  logo: `${SITE.url}/favicon.svg`,
  image: `${SITE.url}/og-image.svg`,
  description:
    "An independent international patient information and coordination platform for plastic surgery in Vietnam. Not a clinic, hospital or medical provider.",
  email: SITE.email,
  telephone: SITE.phone,
  areaServed: ["AU", "GB", "US", "NZ", "FR", "DE", "VN"],
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE.name,
  alternateName: "PSV",
  url: SITE.url,
});

export const webPageSchema = ({ name, description, path }) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  name,
  description,
  url: `${SITE.url}${path}/`.replace(/\/+$/, "/"),
  isPartOf: { "@type": "WebSite", name: SITE.name, url: SITE.url },
  publisher: { "@type": "Organization", name: SITE.name, url: SITE.url },
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE.url}${it.to}`,
  })),
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const articleSchema = ({ title, description, image, path, datePublished, dateModified }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: title,
  description,
  image,
  mainEntityOfPage: path ? `${SITE.url}${path}/`.replace(/\/+$/, "/") : undefined,
  author: { "@type": "Organization", name: `${SITE.name} Editorial Team`, url: SITE.url },
  publisher: { "@type": "Organization", name: SITE.name, url: SITE.url, logo: { "@type": "ImageObject", url: `${SITE.url}/favicon.svg` } },
  datePublished: datePublished || undefined,
  dateModified: dateModified || datePublished || undefined,
});
